import React, { useMemo } from "react";
import { FaChevronLeft, FaEllipsisV } from "react-icons/fa";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import PaidFeatureButton from "../shared/PaidFeatureButton";
import ShowIcon from "../shared/ShowIcon";
import { useAccount, useAccountID } from "../store/account";
import { useShow, useShowID } from "../store/show";
import DropMenu from "../shared/InaccessibleMenu";

// TODO: show flags for filter

const itemBuilder = (accountID, account, showID, show) => {
    // We need to use absolute paths here because this menu gets
    // rendered in two places, and the left nav is not always
    // inside a route for the current show.
    //console.log("itemBuilder", accountID, account, showID, show);
    const items = [
        {
            label: "Show Overview",
            path: `/dashboard/${accountID}/shows/${showID}`,
            end: true,
            enabled: true
        },
        {
            label: show.youTubeShow ? "Views" : "Downloads",
            path: `/dashboard/${accountID}/shows/${showID}/downloads`,
            enabled: show && show.hasMeasurement
        },
        //{
        //    label: "Compare Episodes",
        //    path: `/dashboard/${accountID}/shows/${showID}/episodes`,
        //    enabled: show && show.hasMeasurement && account && account?.isPaidPlan
        //},
        {
            label: "Audience",
            path: `/dashboard/${accountID}/shows/${showID}/audience`,
            enabled: show && show.hasMeasurement && !show.youTubeShow
        },
        {
            label: "Rankings",
            path: `/dashboard/${accountID}/shows/${showID}/rankings`,
            enabled: show && show.hasMeasurement && !show.youTubeShow,
            paid: "The Rankings report is a premium feature available in the Grow Your Show Plan."
        },
        {
            label: "Smartlink",
            path: `/dashboard/${ accountID }/shows/${ showID }/links`,
            enabled: show?.hasMeasurement && account && !account.allowLinkPromotion,
            paid: "Smartlink is a premium feature available in the Grow Your Show Plan."
        },
        {
            label: "Demographics",
            path: `/dashboard/${ accountID }/shows/${ showID }/demographics`,
            enabled: show && show.hasMeasurement && !show.youTubeShow // should probably check survey results?
        },
        {
            label: "Show Settings",
            path: `/dashboard/${accountID}/shows/${showID}/settings`,
            enabled: show?.canViewShowSettings
        }
    ];
    return items.filter(i => i.enabled);
};

export function ShowMenu({ accountID, showID, show }) {
    const { data: account } = useAccount();
    const navigate = useNavigate();
    const items = useMemo(
        () => itemBuilder(accountID, account, showID, show),
        [accountID, account, showID, show]
    );

    return (
        <DropMenu>
            {({
                isOpen,
                createWrapperProps,
                createButtonProps,
                createMenuProps,
                createItemProps
            }) => (
                <span
                    {...createWrapperProps({
                        className: "drop-menu"
                    })}
                >
                    <button
                        {...createButtonProps({
                            className: isOpen ? "active" : undefined
                        })}
                    >
                        <FaEllipsisV />
                        <span className="tip" role="tooltip">
                            More
                        </span>
                    </button>
                    {isOpen ? (
                        <ul {...createMenuProps()}>
                            {items.map(({ label, path, paid }) =>
                                paid ? (
                                    <PaidFeatureButton
                                        key={path}
                                        hasFeature={account?.isPaidPlan}
                                        dialogContent={paid}
                                    >
                                        {({ hasFeature, openModal }) => (
                                            <li
                                                {...createItemProps({
                                                    onClick: hasFeature
                                                        ? () => {
                                                              navigate(path);
                                                          }
                                                        : openModal
                                                })}
                                            >
                                                {label}
                                            </li>
                                        )}
                                    </PaidFeatureButton>
                                ) : (
                                    <li
                                        key={path}
                                        {...createItemProps({
                                            onClick() {
                                                navigate(path);
                                            }
                                        })}
                                    >
                                        {label}
                                    </li>
                                )
                            )}
                        </ul>
                    ) : null}
                </span>
            )}
        </DropMenu>
    );
}

export function ShowTopNav() {
    const accountID = useAccountID();
    const { data: account } = useAccount();
    const showID = useShowID();
    const { data: s = {} } = useShow(accountID, showID);
    const items = useMemo(
        () => itemBuilder(accountID, account, showID, s),
        [accountID, account, showID, s]
    );

    return (
        <>
            <Link className="back" to={`/dashboard/${accountID}`}>
                <FaChevronLeft />
            </Link>
            <ShowIcon large showImageUrl={s.showImageUrl} />
            <nav>
                <h1>{s.showName || "\u2001"}</h1>
                <ul>
                    {items.map(({ path, label, end, paid }) => (
                        <li key={path}>
                            {paid ? (
                                <PaidFeatureButton
                                    hasFeature={account?.isPaidPlan}
                                    dialogContent={paid}
                                >
                                    {({ hasFeature, openModal }) => (
                                        <NavLink
                                            onClick={hasFeature ? () => {} : openModal}
                                            to={path}
                                            end={end}
                                        >
                                            {label}
                                        </NavLink>
                                    )}
                                </PaidFeatureButton>
                            ) : (
                                <NavLink to={path} end={end}>
                                    {label}
                                </NavLink>
                            )}
                        </li>
                    ))}
                </ul>
            </nav>
        </>
    );
}
